import dayjs from 'dayjs';
import {ContractStatus, type Contract} from '@/ts/types/dto/user-dto';
import {LicenseProvisionTypeDTO} from '@/ts/types/dto/sign-in-dto';

/**
 * The CDC DPP runs for a full year; access shorter than this can't see the user through the
 * programme, so the fallback steps in. Kept in sync with the "Balm One-Year Gifted" license.
 */
export const PROGRAM_DURATION_DAYS = 365;

/**
 * True when the contract already gets the user through the full programme — lifetime validity
 * (`validityPeriodInDays` null), or an expiry at least a programme-length away. Paying subscribers
 * are a separate matter: their access renews, so days-left doesn't model it (a monthly licence is
 * indefinite at the licence level and the real expiry lives in the Stripe period).
 *
 * Requires the contract to be ACTIVE: an expired/cancelled contract (or none at all) covers
 * nothing, so it must NOT route the user to the "your access already covers the programme" info
 * terminal — they belong on the upgrade / free-year offer path instead.
 */
export function accessCoversProgram(contract: Contract | null | undefined): boolean {
  if (contract?.status !== ContractStatus.ACTIVE) {
    return false;
  }
  const validity = contract.license.validityPeriodInDays as number | null | undefined;
  if (typeof validity !== 'number') {
    return true;
  }
  // Compare at DAY granularity. Using days-remaining (`diff(now, 'day')`, which truncates) made the
  // result depend on the time of day and made an exactly-programme-long validity (e.g. a 365-day
  // voucher created at 11:37 today) read as 364 → one day short. Covered iff the access expiry day
  // is on or after the day the programme would end if it started today.
  const expiryDay = dayjs(contract.createDate).add(validity, 'day').startOf('day');
  const programmeEndDay = dayjs().add(PROGRAM_DURATION_DAYS, 'day').startOf('day');
  return !expiryDay.isBefore(programmeEndDay);
}

/**
 * True when the user already holds programme-long coverage that was *granted* to them — either
 * insurance-funded, or a gift that covers the year (the one-year reward, or an indefinite lifetime
 * gift). This is deliberately NARROWER than {@link accessCoversProgram}: a paying subscriber or a
 * voucher holder also covers the programme, but we still want to offer them the insurance route, so
 * they are excluded here.
 *
 * For a gift it checks the licence's **own validity span** (lifetime, or `>= PROGRAM_DURATION_DAYS`)
 * rather than days-remaining: the one-year reward lasts *exactly* the programme length, so a
 * freshly-granted contract reads as 364 days left moments after the grant and would wrongly fail a
 * days-remaining `>= 365` check. A gift issued to span the programme keeps covering it.
 *
 * Used to suppress the CDC DPP eligibility gate for users whose programme access is already a
 * settled grant — their funding decision is made regardless of the recorded acknowledgement, so
 * re-prompting them adds no decision. See {@link useCdcDppEligibility}.
 */
export function hasGrantedProgramCoverage(contract: Contract | null | undefined): boolean {
  if (contract?.status !== ContractStatus.ACTIVE) {
    return false;
  }
  const {provisionType} = contract.license;
  if (provisionType === LicenseProvisionTypeDTO.INSURANCE_BACKED) {
    return true;
  }
  if (provisionType !== LicenseProvisionTypeDTO.GIFTED) {
    return false;
  }
  const validity = contract.license.validityPeriodInDays as number | null | undefined;
  return typeof validity !== 'number' || validity >= PROGRAM_DURATION_DAYS;
}
